/**
 * Feb 2026 · Stock Ledger · Kardex-style read-only ledger for one RM.
 *
 * Chronological transactions with running balance for a chosen Raw
 * Material and date range. Reads from `/api/inventory/stock-book` which
 * is backed by the same `inventory_txns` (a.k.a. inventory_ledger)
 * collection that powers the Inventory Log — guaranteeing both views
 * show identical, high-integrity data.
 *
 * READ-ONLY: every stock change must originate from Opening Stock, GRN,
 * or Material Issue. No manual Add / Adjust affordances here.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  BookOpenIcon, MagnifyingGlassIcon, DownloadSimpleIcon, PrinterIcon,
  ArrowsClockwiseIcon,
} from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csv";

const inpCls = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";

const badgeCls = (t) => {
  if (t.startsWith("Inward") || t === "Adjust +" || t === "Transfer In") return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
  if (t.startsWith("Outward") || t === "Adjust -" || t === "Transfer Out") return "bg-red-500/15 text-red-300 border-red-500/30";
  if (t.startsWith("Transfer")) return "bg-sky-500/15 text-sky-300 border-sky-500/30";
  return "bg-zinc-500/15 text-zinc-400 border-zinc-500/30";
};

export default function StockBook() {
  const [rms, setRms] = useState([]);
  const [rmId, setRmId] = useState("");
  const [rmSearch, setRmSearch] = useState("");
  const [from_, setFrom] = useState("");
  const [to_, setTo] = useState("");
  const [batch, setBatch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/raw-materials/");
        setRms((data || []).filter(r => r.is_active !== false));
      } catch (_e) { /* silent */ }
    })();
  }, []);

  const filteredRms = useMemo(() => {
    const needle = rmSearch.trim().toLowerCase();
    if (!needle) return rms.slice(0, 30);
    return rms.filter(r => [r.code, r.name, r.category].filter(Boolean).join(" ").toLowerCase().includes(needle)).slice(0, 50);
  }, [rms, rmSearch]);

  const run = async () => {
    if (!rmId) { toast.error("Pick a Raw Material first"); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/stock-book", {
        params: { rm_id: rmId, from_date: from_ || undefined, to_date: to_ || undefined, batch_no: batch.trim() || undefined },
      });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Stock Ledger failed");
    } finally { setLoading(false); }
  };

  const totalIn = (data?.rows || []).reduce((s, r) => s + r.in_qty, 0);
  const totalOut = (data?.rows || []).reduce((s, r) => s + r.out_qty, 0);

  const exportCsv = () => {
    if (!data) return;
    exportToCSV(`stock-ledger-${data.rm.code}.csv`, data.rows.map(r => ({
      date: r.txn_date, type: r.txn_type,
      mi_no: r.mi_no || "", mir_no: r.mrq_no || "",
      reference: r.reference,
      batch: r.batch_no || "",
      supplier: r.supplier_name, godown: r.godown,
      so_no: r.source_order_no || "", cwo_no: r.cwo_no || "", dept: r.department || "",
      in_qty: r.in_qty, out_qty: r.out_qty, balance: r.balance, remarks: r.remarks,
    })), [
      { key: "date", label: "Date" }, { key: "type", label: "Type" },
      { key: "mi_no", label: "MI No" },
      { key: "mir_no", label: "MIR No" },
      { key: "reference", label: "Document Reference" },
      { key: "batch", label: "Batch No" },
      { key: "supplier", label: "Supplier" },
      { key: "godown", label: "Godown" },
      { key: "so_no", label: "SO/ESO" }, { key: "cwo_no", label: "CWO" }, { key: "dept", label: "Dept" },
      { key: "in_qty", label: "In" },
      { key: "out_qty", label: "Out" }, { key: "balance", label: "Balance" },
      { key: "remarks", label: "Remarks" },
    ]);
  };

  const printPdf = () => {
    if (!data) return;
    const w = window.open("", "_blank", "width=1200,height=900");
    if (!w) { toast.error("Popup blocked — allow popups for this site"); return; }
    const rowsHtml = data.rows.map(r => `
      <tr>
        <td>${r.txn_no || "—"}</td>
        <td>${r.txn_date}</td>
        <td>${r.txn_type}</td>
        <td>${r.mi_no ? `<b>${r.mi_no}</b>${r.mrq_no && r.mrq_no !== r.mi_no ? `<br><span style="font-size:8px;color:#666">MIR: ${r.mrq_no}</span>` : ""}` : (r.reference || "—")}</td>
        <td>${r.batch_no || "—"}</td>
        <td>${r.source_order_no || "—"}</td>
        <td>${r.cwo_no || "—"}</td>
        <td>${r.godown}</td>
        <td class="num">${r.in_qty || ""}</td>
        <td class="num">${r.out_qty || ""}</td>
        <td class="num"><b>${r.balance}</b></td>
      </tr>
    `).join("");
    w.document.write(`<!doctype html><html><head><title>Stock Ledger · ${data.rm.code}</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body { font-family: -apple-system, sans-serif; font-size: 10px; color: #111; }
        h1 { margin: 0 0 4px; font-size: 16px; }
        .meta { color: #666; font-size: 10px; margin-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border-bottom: 1px solid #ddd; padding: 4px 6px; text-align: left; }
        th { background: #f4f4f4; text-transform: uppercase; font-size: 9px; letter-spacing: .05em; }
        td.num, th.num { text-align: right; font-family: 'Courier New', monospace; }
        tfoot td { font-weight: bold; border-top: 2px solid #000; }
      </style></head><body>
      <h1>Stock Ledger · ${data.rm.code} · ${data.rm.name}</h1>
      <div class="meta">UOM ${data.rm.uom || ""} · Period ${data.from_date || "opening"} to ${data.to_date || "latest"}${batch ? ` · Batch <b>${batch}</b>` : ""} · Opening ${data.opening_balance} · Closing ${data.closing_balance}</div>
      <table>
        <thead><tr><th>Txn No</th><th>Date</th><th>Type</th><th>Document Reference</th><th>Batch No</th><th>SO/ESO</th><th>CWO</th><th>Godown</th><th class="num">In</th><th class="num">Out</th><th class="num">Balance</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot><tr><td colspan="8">Totals</td><td class="num">${totalIn.toFixed(3)}</td><td class="num">${totalOut.toFixed(3)}</td><td class="num">${data.closing_balance}</td></tr></tfoot>
      </table>
      <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),300)};</script>
      </body></html>`);
    w.document.close();
  };

  const selectedRm = rms.find(r => r.id === rmId);

  return (
    <div className="p-6 space-y-5" data-testid="stock-ledger-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Inventory · Ledger</div>
          <h1 className="font-display text-3xl font-black tracking-tight flex items-center gap-3">
            <BookOpenIcon size={26} weight="bold" className="text-[var(--accent)]" />
            Stock Ledger
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Read-only audit trail · Kardex-style chronological ledger for any Raw Material · same <span className="text-zinc-300 font-mono">inventory_ledger</span> source as the Inventory Log. Every row originates from Opening Stock, a GRN, or a Material Issue — no manual Add or Adjust here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="sb-export-csv" onClick={exportCsv} disabled={!data}
            className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm flex items-center gap-2 disabled:opacity-40">
            <DownloadSimpleIcon size={13} weight="bold" />Excel (CSV)
          </button>
          <button data-testid="sb-print-pdf" onClick={printPdf} disabled={!data}
            className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm flex items-center gap-2 disabled:opacity-40">
            <PrinterIcon size={13} weight="bold" />Print / PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="tactical-card p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Raw Material *</label>
          <div className="relative">
            <MagnifyingGlassIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input data-testid="sb-rm-search" value={rmSearch} onChange={e => setRmSearch(e.target.value)}
              placeholder="Search by RM Code / Name…"
              className={`${inpCls} pl-9`} />
          </div>
          <select data-testid="sb-rm-picker" value={rmId} onChange={e => setRmId(e.target.value)}
            className={`${inpCls} mt-2 font-mono`}>
            <option value="">— select —</option>
            {filteredRms.map(r => (
              <option key={r.id} value={r.id}>{r.code} · {r.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">From Date</label>
          <input data-testid="sb-from" type="date" value={from_} onChange={e => setFrom(e.target.value)} className={inpCls} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">To Date</label>
          <input data-testid="sb-to" type="date" value={to_} onChange={e => setTo(e.target.value)} className={inpCls} />
        </div>
        <div className="md:col-span-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Batch No <span className="text-zinc-600 normal-case">(optional — pull txns for a specific batch)</span></label>
            <input data-testid="sb-batch" value={batch} onChange={e => setBatch(e.target.value)} placeholder="e.g. BH-503" className={`${inpCls} font-mono`} />
          </div>
        </div>
        <div className="md:col-span-4 flex items-center gap-2">
          <button data-testid="sb-run" onClick={run} disabled={!rmId || loading}
            className="h-10 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2 disabled:opacity-40">
            <ArrowsClockwiseIcon size={13} weight="bold" />{loading ? "Loading…" : "Show Ledger"}
          </button>
          {/* Feb 27, 2026 · Clear button — resets RM code, batch, dates,
              and any previously shown ledger data so the operator can
              verify a fresh material without page-reload. */}
          <button data-testid="sb-clear" type="button"
            onClick={() => { setRmId(""); setBatch(""); setFrom(""); setTo(""); setData(null); }}
            disabled={!rmId && !batch && !from_ && !to_ && !data}
            className="h-10 px-4 border border-[var(--border)] text-zinc-300 hover:bg-[var(--bg-2)] hover:border-red-500/40 hover:text-red-300 text-sm font-semibold rounded-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            Clear
          </button>
          {selectedRm && (
            <span className="text-xs text-zinc-500 font-mono">
              Current stock: <span className="text-white">{Number(selectedRm.current_stock || 0).toLocaleString("en-IN")}</span> {selectedRm.uom}
              {" · Min: "}<span className="text-amber-300">{Number(selectedRm.min_stock || 0)}</span>
              {batch && <> · Batch <span className="text-white">{batch}</span></>}
            </span>
          )}
        </div>
      </div>

      {/* Summary + ledger */}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="tactical-card p-4"><div className="overline mb-1">Opening Balance</div><div className="font-mono text-2xl font-semibold text-zinc-300" data-testid="sb-opening">{data.opening_balance}</div></div>
            <div className="tactical-card p-4 border-l-2 border-l-emerald-500"><div className="overline text-emerald-300 mb-1">Total In</div><div className="font-mono text-2xl font-semibold text-emerald-400">{totalIn.toFixed(3)}</div></div>
            <div className="tactical-card p-4 border-l-2 border-l-red-500"><div className="overline text-red-300 mb-1">Total Out</div><div className="font-mono text-2xl font-semibold text-red-400">{totalOut.toFixed(3)}</div></div>
            <div className="tactical-card p-4 border-l-2 border-l-sky-500"><div className="overline text-sky-300 mb-1">Closing Balance</div><div className="font-mono text-2xl font-semibold text-sky-400" data-testid="sb-closing">{data.closing_balance}</div></div>
            <div className="tactical-card p-4"><div className="overline mb-1">Transactions</div><div className="font-mono text-2xl font-semibold text-white">{data.rows.length}</div></div>
          </div>

          {/* Feb 2026 · Godown-wise Balance for the selected RM — pulled
              from stock_movements so we can see *where* stock currently
              sits (RM-STORE / floor godowns) at a glance. */}
          {(data.by_godown || []).length > 0 && (
            <div className="tactical-card p-4 space-y-3" data-testid="sb-by-godown">
              <div className="flex items-center justify-between">
                <div className="overline">Godown-wise Balance · {data.rm.code}</div>
                <div className="text-[10px] font-mono text-zinc-500">Grand Total: <span className="text-white">{data.by_godown.reduce((s, g) => s + Number(g.balance || 0), 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</span> {data.rm.uom || ""}</div>
              </div>
              <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
                    <tr>
                      <th className="px-3 py-2 text-left">Godown</th>
                      <th className="px-3 py-2 text-right">In</th>
                      <th className="px-3 py-2 text-right">Out</th>
                      <th className="px-3 py-2 text-right">Balance</th>
                      <th className="px-3 py-2 text-right">Moves</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_godown.map((g, i) => (
                      <tr key={i} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30" data-testid={`sb-godown-row-${i}`}>
                        <td className="px-3 py-2 text-white">{g.godown_name}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300">{Number(g.in_qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-red-300">{Number(g.out_qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</td>
                        <td className={`px-3 py-2 text-right font-mono tabular-nums font-semibold ${Number(g.balance) < 0 ? "text-red-400" : "text-white"}`}>{Number(g.balance || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-400">{g.moves}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="tactical-card p-4 space-y-3">
            <div className="text-xs text-zinc-500">
              {data.rm.code} · {data.rm.name} · UOM <span className="text-zinc-300 font-mono">{data.rm.uom}</span>
              {(data.from_date || data.to_date) && <> · Period <span className="font-mono text-zinc-300">{data.from_date || "opening"} → {data.to_date || "latest"}</span></>}
            </div>
            <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px] sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Document Reference</th>
                    <th className="px-3 py-2 text-left">Batch No</th>
                    <th className="px-3 py-2 text-left">SO / ESO</th>
                    <th className="px-3 py-2 text-left">CWO / Dept</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Godown</th>
                    <th className="px-3 py-2 text-right">In</th>
                    <th className="px-3 py-2 text-right">Out</th>
                    <th className="px-3 py-2 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 && (
                    <tr><td colSpan={11} className="text-center py-8 text-zinc-500 text-xs">No transactions in this period{batch ? ` for batch ${batch}` : ""}.</td></tr>
                  )}
                  {data.rows.map((r, i) => {
                    // Feb 25 · Reversal badge · REVERSAL_* txn_types are
                    // audit-preserving mirrors of an original leg. Amber
                    // pill so QA spots them at a glance.
                    const isReversal = ((r.txn_type || "").toString().startsWith("REVERSAL_")
                      || /^Reversal of/i.test(r.remarks || "")
                      || /^Reversal of/i.test(r.reference || "")
                      || (r.txn_type === "Adjust" && /Reversal of/i.test(r.remarks || "")));
                    return (
                    <tr key={i} data-testid={`sb-row-${i}`} className={`border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30 ${isReversal ? "bg-amber-500/5" : ""}`}>
                      <td className="px-3 py-2 font-mono text-xs text-zinc-400 whitespace-nowrap">{r.txn_date}</td>
                      <td className="px-3 py-2">
                        <div className="inline-flex items-center gap-1.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${badgeCls(r.txn_type)}`}>{r.txn_type}</span>
                          {isReversal && (
                            <span
                              data-testid={`sb-reversal-${i}`}
                              title="Audit-preserving reversal of an earlier transaction"
                              className="inline-flex items-center px-1.5 py-0.5 rounded-sm border border-amber-500/50 bg-amber-500/10 text-amber-300 text-[9px] font-mono uppercase tracking-widest"
                            >Reversal</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs font-mono">
                        {r.mi_no ? (
                          <div>
                            <div className="text-emerald-300 font-bold" data-testid={`sb-mi-${i}`}>{r.mi_no}</div>
                            {r.mrq_no && r.mrq_no !== r.mi_no && <div className="text-[9px] text-zinc-500 mt-0.5">MIR: {r.mrq_no}</div>}
                          </div>
                        ) : (
                          <span className="text-zinc-300">{r.reference || "—"}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-zinc-200" data-testid={`sb-batch-${i}`}>{r.batch_no || <span className="text-zinc-600">—</span>}</td>
                      <td className="px-3 py-2 text-xs font-mono text-amber-300" data-testid={`sb-so-${i}`}>{r.source_order_no || "—"}</td>
                      <td className="px-3 py-2 text-xs font-mono text-sky-300">
                        <div>{r.cwo_no || "—"}</div>
                        {r.department && <div className="text-[10px] text-zinc-500">{r.department}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-400">{r.supplier_name || "—"}</td>
                      <td className="px-3 py-2 text-xs">{r.godown}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300">{r.in_qty || ""}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-red-300">{r.out_qty || ""}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-white">{r.balance}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
